import {
  Activity,
  ArrowLeftRight,
  Briefcase,
  Building2,
  CalendarClock,
  Coins,
  FilePlus,
  FileSpreadsheet,
  FileText,
  HandCoins,
  Home,
  LayoutDashboard,
  Landmark,
  Megaphone,
  Repeat,
  Settings,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import { P } from '@massar/shared/permissions';
import { MODULE, SECTION_MODULE_KEYS } from '@massar/shared/modules';
import { SECTION_SLUGS } from '@massar/shared/constants';

/**
 * The navigation tree, defined once (docs/04 §2) and filtered in the shell.
 *
 * Two independent filters run, and they answer different questions (docs/03 §2a):
 *
 *   **permission** — may *this user* do it?     ← the role, granted by an administrator
 *   **module**     — does *this section* do it? ← configuration, set for the business line
 *
 * Both must pass. A Real Estate section manager holds `properties.property.manage` —
 * every section-scoped manager does, it is one role object — and still must not see a
 * property register, because Real Estate does not run that module (docs/10 §4.2).
 * Filtering on the permission alone
 * would put the item there; filtering on the module alone would put it in front of a
 * user with no grant.
 *
 * Item contract:
 *   `permission`  — a single permission string; the item is hidden without it.
 *   `anyOf`       — shown if the user holds *any* of these (used where one screen serves
 *                   two roles, e.g. a report a treasurer and an accountant both read).
 *   `module`      — the module that owns the screen. Hidden unless the current scope
 *                   runs it. Group-scoped modules (`admin`, `dashboard`) are never
 *                   filtered — they are not a section's to switch off.
 *   `global`      — the item is group-wide; the permission is checked without a section,
 *                   because a section-scoped holder must still not see it.
 *
 * Group contract:
 *   `sections`    — the business lines this group belongs to, by slug. Only the four
 *                   **section verticals** carry it, and it is a different question from
 *                   `module`: a module is configuration and can be switched off, a
 *                   section's identity cannot. Real Estate is the case that proves they
 *                   are not the same — it runs no vertical module at all (docs/10 §4.2),
 *                   so there is no module whose presence could mean "this is the Real
 *                   Estate menu". Absent → the group is for every section.
 *   `alwaysGroup` — keep the heading even when one item survives filtering. On a vertical
 *                   the heading *is* the business line's name, so dropping it to save a
 *                   row would take away the one thing that says whose screens these are.
 *
 * Only screens that exist today are listed. Later phases add their own groups here as
 * the modules that back them land — an item pointing at an unbuilt route is a broken
 * link, not a roadmap.
 */
/**
 * The four business lines, in the order the group thinks of them, each with the icon its
 * own menu already uses — so *Construction Report* under Reports and *Construction* in the
 * sidebar above it are recognisably the same thing.
 *
 * Read off `SECTION_SLUGS` rather than written out, minus HQ: head office keeps the books,
 * it is not a business line with a register of its own, and its documents show on the
 * General Report under the group column.
 */
const SECTION_REPORT_SLUGS = [
  SECTION_SLUGS.CONSTRUCTION,
  SECTION_SLUGS.BROKERS,
  SECTION_SLUGS.REAL_ESTATE,
  SECTION_SLUGS.AGENCY,
  SECTION_SLUGS.CLEANER,
];

const SECTION_REPORT_ICONS = {
  [SECTION_SLUGS.CONSTRUCTION]: Building2,
  [SECTION_SLUGS.BROKERS]: Briefcase,
  [SECTION_SLUGS.REAL_ESTATE]: Home,
  [SECTION_SLUGS.AGENCY]: Megaphone,
  [SECTION_SLUGS.CLEANER]: Sparkles,
};

export const NAVIGATION = [
  {
    key: 'dashboard',
    /* `always`: the shell's own row, kept when a section narrows the menu to itself. Every
       section is entered from here, so it is the one link that must never disappear. */
    always: true,
    items: [{ key: 'overview', to: '/', icon: LayoutDashboard, end: true, module: MODULE.DASHBOARD }],
  },
  /*
   * ── The section's own vertical — one group per business line ──────────────────────
   *
   * These lead, and that ordering is the point of the V2 restructure (docs/10 §1): the
   * update was written as a *section-first* menu — Construction gets Properties, Brokers
   * gets Brokers and Treats — with the shared finance screens underneath rather than the
   * other way round. Exactly one of these four is ever on screen, because `sections`
   * resolves to the section in scope, so leading with them costs the other sections
   * nothing.
   *
   * **All four are named after the business line**, and each one lists the screens
   * docs/10 §1 asks that line for. A section whose vertical is a *module* (Brokers,
   * Construction) had one already; Real Estate and Agency did not, because neither owns a
   * module the menu could key off — Real Estate deliberately runs no vertical (§4.2) and
   * the Agency vertical's own screens (service contracts, campaigns) have no UI yet. That
   * is why the group is pinned by **slug** and each *item* still carries the module that
   * owns it: the heading says whose section this is, the module gate says which of its
   * screens are switched on, and turning Invoices off on Agency still empties the row
   * rather than leaving a link that 403s.
   *
   * Shared screens appearing here *and* under Income/Outcome is intended, not an
   * oversight: §1 lists Invoices and Customers under the sections that live in them, and
   * the finance groups below are the same documents read by direction rather than by
   * business line.
   */
  {
    key: 'construction',
    icon: Building2,
    sections: [SECTION_SLUGS.CONSTRUCTION],
    alwaysGroup: true,
    items: [
      /*
       * Construction's register (docs/10 §4.1). Gated on MODULE.PROPERTIES, which only the
       * Construction section enables — Real Estate holds the same grants and runs none of it.
       */
      {
        key: 'propertyRegister',
        to: '/properties',
        icon: Building2,
        permission: P.PROP_PROPERTY_VIEW,
        module: MODULE.PROPERTIES,
      },
      {
        /*
         * **The owners Construction builds for** (docs/10 §4.1a) — and what is left of the
         * money each of them paid in. Part of the property register's module, because an
         * owner exists because a property does.
         */
        key: 'cnOwners',
        to: '/owners',
        icon: HandCoins,
        permission: P.PROP_OWNER_VIEW,
        module: MODULE.PROPERTIES,
      },
      {
        /* One owner's money, movement by movement, with Excel and PDF on it. */
        key: 'cnOwnerReport',
        to: '/owners/statement',
        icon: FileSpreadsheet,
        permission: P.PROP_OWNER_VIEW,
        module: MODULE.PROPERTIES,
      },
      /*
       * ── Narrowed to properties, owners and the owner report ──────────────────────────
       *
       * Asked for directly: Construction's day is registering a property, recording whose
       * money is behind it, and reading that money back. The six items below are the rest
       * of §1's Construction list and they are **commented, not deleted** — every route,
       * screen, service and test behind them is untouched and passing, and a group that
       * starts selling units on contract uncomments the block.
       *
       * The same treatment §9b gave the deal-era Brokers screens, for the same reason: a
       * menu is a statement about what a section does today, and hiding a working screen is
       * a configuration decision rather than a reason to throw code away.
       *
       * Sale contracts and leases are still reachable at `/properties/sale-contracts` and
       * `/properties/leases`; invoices, customers and the P&L are still on the finance
       * groups further down, which is where a Construction accountant reads them by
       * direction rather than by business line.
       */
      // {
      //   key: 'saleContracts',
      //   to: '/properties/sale-contracts',
      //   icon: FileText,
      //   permission: P.PROP_CONTRACT_VIEW,
      //   module: MODULE.PROPERTIES,
      // },
      // {
      //   key: 'leases',
      //   to: '/properties/leases',
      //   icon: CalendarRange,
      //   permission: P.PROP_CONTRACT_VIEW,
      //   module: MODULE.PROPERTIES,
      // },
      // {
      //   // Who bought, and what they still owe — per currency, never summed (docs/10 §5.3).
      //   key: 'cnCustomers',
      //   to: '/customers',
      //   icon: Users,
      //   permission: P.PARTY_VIEW,
      //   module: MODULE.PARTIES,
      // },
      // {
      //   // Both kinds on one screen: a section that sells units also buys materials, and
      //   // the direction filter is on the page.
      //   key: 'cnInvoices',
      //   to: '/invoices',
      //   icon: FileText,
      //   permission: P.INVOICE_VIEW,
      //   module: MODULE.INVOICES,
      // },
      // {
      //   // "Customer Report" (§1). Same two-route shape as the Broker Report: a menu item
      //   // cannot carry an id, so this one asks which customer and the register's row
      //   // answers it (docs/10 §9b).
      //   key: 'cnCustomerReport',
      //   to: '/customers/statement',
      //   icon: FileSpreadsheet,
      //   permission: P.PARTY_VIEW,
      //   module: MODULE.PARTIES,
      // },
      // {
      //   // "General Report" (§1) — the section's own P&L. Not `global`: a Construction
      //   // accountant reads Construction's, and the consolidated column is a grant the
      //   // service checks per request, not a second screen.
      //   key: 'cnReport',
      //   to: '/reports/income-statement',
      //   icon: BarChart3,
      //   permission: P.REPORT_VIEW,
      //   module: MODULE.REPORTS,
      // },
    ],
  },
  {
    key: 'brokers',
    icon: Briefcase,
    sections: [SECTION_SLUGS.BROKERS],
    alwaysGroup: true,
    items: [
      {
        // The register *and* the money view — balances per currency, and the terms that
        // price his work, on one screen (docs/10 §3).
        key: 'brokerProfiles',
        to: '/brokers/profiles',
        icon: Users,
        permission: P.BR_PROFILE_VIEW,
        module: MODULE.BROKERS,
      },
      {
        // Buying and selling with a broker's money. Reading is the wider grant, so a
        // broker can watch his own positions without being able to move them.
        key: 'treats',
        to: '/brokers/treats',
        icon: Repeat,
        permission: P.BR_DEAL_VIEW,
        module: MODULE.BROKERS,
      },
      {
        // *Every transaction that happened on this broker* — the report the group asked
        // for. Not `global`: it is one section's ledger, read by that section.
        key: 'brokerReport',
        to: '/brokers/report',
        icon: FileSpreadsheet,
        permission: P.BR_CAPITAL_VIEW,
        module: MODULE.BROKERS,
      },
      /*
       * ── The deal-era screens, off the menu ──────────────────────────────────────────
       *
       * All three belong to the brokerage V2 inverted (docs/10 §3): the company brokered
       * somebody else's sale, earned a commission on it, and settled that commission in a
       * payout. Treats replaced the whole arrangement — the broker brings his own money and
       * the company takes a share of the profit — so on a V2 install these screens describe
       * a business that is not being done:
       *
       *   `deals`             a pipeline nothing enters any more; Treats is where a broker's
       *                       money is now put to work.
       *   `brokerPayouts`     settles commissions raised by closing a deal. What a broker is
       *                       paid today leaves through capital **withdraw** instead.
       *   `dealProfitability` reports **closed deals** and is therefore permanently empty —
       *                       it was not failing, it was answering about a model with no
       *                       rows in it. The Broker Report is the profit view now: it shows
       *                       each treat's gross profit, the company's commission and his
       *                       share.
       *
       * Commented rather than deleted, and the routes, services and their 16 passing tests
       * are all untouched — a group still running deals uncomments these four blocks and has
       * them back. They are hidden because a menu item leading to a screen that can only be
       * empty is worse than no menu item (docs/04 §2).
       */
      // {
      //   key: 'deals',
      //   to: '/brokers/deals',
      //   icon: Briefcase,
      //   permission: P.BR_DEAL_VIEW,
      //   module: MODULE.BROKERS,
      // },
      // {
      //   key: 'brokerPayouts',
      //   to: '/brokers/payouts',
      //   icon: HandCoins,
      //   permission: P.BR_PAYOUT_MANAGE,
      //   module: MODULE.BROKERS,
      // },
      // {
      //   key: 'dealProfitability',
      //   to: '/reports/deal-profitability',
      //   icon: BarChart3,
      //   permission: P.BR_DEAL_VIEW_MARGIN,
      //   module: MODULE.BROKERS,
      // },
    ],
  },
  {
    /*
     * Real Estate is **thin** by decision, not by omission (docs/10 §4.2): invoices both
     * ways, the employee who earned the commission on each, and a report. No properties
     * and no contracts — that register moved to Construction with the module.
     */
    key: 'realestate',
    icon: Home,
    sections: [SECTION_SLUGS.REAL_ESTATE],
    alwaysGroup: true,
    items: [
      // {
      //   /*
      //    * **One item, both directions.** §1 asks Real Estate for "invoices (income +
      //    * outcome)" and that is one register read two ways, not two registers: the list
      //    * already carries a kind filter, and splitting the menu made an operator choose
      //    * which screen to open before they had chosen what to record. The finance groups
      //    * below still offer the filtered links, because down there the *direction* is the
      //    * organising idea rather than the section.
      //    */
      //   key: 'reInvoices',
      //   to: '/invoices',
      //   icon: FileText,
      //   permission: P.INVOICE_VIEW,
      //   module: MODULE.INVOICES,
      // },
      // {
      //   /*
      //    * §1 asks that a Real Estate invoice show "the employee who earns commission" —
      //    * `Invoice.earnedByPartyIds`, built in step 8 and widened to several names in step
      //    * 14. The invoice names them; this is where that becomes money owed, so the two belong
      //    * in the same block.
      //    */
      //   key: 'reCommissions',
      //   to: '/commissions',
      //   icon: Percent,
      //   permission: P.COMMISSION_VIEW,
      //   module: MODULE.COMMISSIONS,
      // },
      {
        /*
         * **Treats** (docs/10 §4.2a) — buying a property with the company's own money and
         * selling it on. The one thing this section does that no shared module covers,
         * which is why §4.2's "Real Estate is thin" now has exactly one vertical item.
         *
         * Its own module, not `properties`: that key means Construction's register since
         * §4.1, and enabling it here to reach this screen would hand Real Estate a
         * property list the restructure deliberately moved away from it.
         */
        key: 'reTreats',
        to: '/treats',
        icon: ArrowLeftRight,
        permission: P.RE_TREAT_VIEW,
        module: MODULE.TREATS,
      },
      {
        /*
         * **New invoice** — the section's own form (docs/10 §4.2d).
         *
         * A second item beside the register rather than a replacement for it, and beside
         * rather than inside because the register is the shared `/invoices` screen: it
         * already reads both directions and filters by section, and pointing the menu at a
         * Real-Estate-only list would be a second register of the same documents. The form
         * is the part that is this section's own.
         */
        key: 'reNewInvoice',
        to: '/realestate/invoices/new',
        icon: FilePlus,
        permission: P.INVOICE_CREATE,
        module: MODULE.INVOICES,
      },
      {
        /*
         * **Tenancies** (docs/10 §4.2c) — the terms behind the Tenants register: who rents
         * what, until when, on what rhythm, and the button that bills a month when the
         * tenant pays.
         *
         * Gated on `parties` like Tenants itself, because a tenancy is that register's
         * agreement — and because a module key invented for it would be absent from every
         * already-seeded section and silently hide the feature (see the module entry).
         */
        key: 'reTenancies',
        to: '/tenancies',
        icon: CalendarClock,
        permission: P.RE_TENANCY_VIEW,
        module: MODULE.PARTIES,
      },
      {
        /*
         * **Tenants** (docs/10 §4.2b) — the people Real Estate bills rent to.
         *
         * The register this section was missing: it raises `rent` invoices against parties
         * with the `tenant` role and had nowhere to see them, so "who is behind?" meant
         * reading the invoice list and recognising names. Gated on `parties`, because a
         * tenant *is* a party — this screen is the financial view of one, not a second
         * place to create it.
         */
        key: 'reTenants',
        to: '/tenants',
        icon: Users,
        permission: P.PARTY_VIEW,
        module: MODULE.PARTIES,
      },
      {
        /* The treats register — bought, sold, and what each one made. */
        key: 'reTreatReport',
        to: '/reports/treats',
        icon: TrendingUp,
        permission: P.RE_TREAT_VIEW,
        module: MODULE.TREATS,
      },
      {
        /*
         * **The invoice register, not the P&L.** Real Estate's own question is "what have we
         * billed and what is still outstanding", and the income statement answers a different
         * one — it nets the ledger, includes everything that is not an invoice, and cannot be
         * reconciled against the documents a manager actually signed. The section P&L is
         * still on the Reports group below for anyone who wants it.
         */
        key: 'reInvoiceReport',
        to: '/reports/invoices',
        icon: FileSpreadsheet,
        permission: P.REPORT_VIEW,
        module: MODULE.REPORTS,
      },
    ],
  },
  {
    /*
     * Agency (docs/10 §4.3): invoices, customers, a report and a per-customer report. The
     * vertical's own screens — service contracts, retainers, campaigns, deliverables —
     * are built on the server and have no UI yet, so they are deliberately absent: a menu
     * item pointing at an unbuilt route is a broken link, not a roadmap (docs/04 §2).
     */
    key: 'agency',
    icon: Megaphone,
    sections: [SECTION_SLUGS.AGENCY],
    alwaysGroup: true,
    items: [
      {
        /*
         * Agency's own register and form (docs/10 §4.3), not the shared invoice screens.
         * The section records money moving against one customer — customer, reason, amount,
         * note — and the general form asks eleven more questions it never answers.
         */
        key: 'agInvoices',
        to: '/agency/invoices',
        icon: FileText,
        permission: P.INVOICE_VIEW,
        module: MODULE.INVOICES,
      },
      {
        // The same balance register, carrying the two buttons that move a balance.
        key: 'agCustomers',
        to: '/agency/customers',
        icon: Users,
        permission: P.PARTY_VIEW,
        module: MODULE.PARTIES,
      },
      {
        key: 'agCustomerReport',
        to: '/customers/statement',
        icon: FileSpreadsheet,
        permission: P.PARTY_VIEW,
        module: MODULE.PARTIES,
      },
      /*
       * **Section report — hidden, not deleted**, the same call as the Construction items
       * above and for the same reason: the menu is a statement about what a section does
       * today, and hiding a working screen is a configuration decision rather than a
       * reason to throw code away.
       *
       * It pointed at `/reports/income-statement`, which is the group-wide P&L narrowed by
       * the section switcher — so it is not an Agency screen that has gone missing. The
       * same route is still on the **Reports** group below, where an accountant reads it
       * alongside the trial balance and the balance sheet.
       *
       * Its `BarChart3` import went with it — this was the last *live* use of that icon,
       * and lint does not keep one alive for commented code. The same is true of the six
       * that went with the Income, Outcome, Treasury and Accounting groups below:
       * `BookOpen`, `CalendarRange`, `Clock`, `Receipt`, `ReceiptText` and `PieChart`.
       * **Re-enabling any commented item means adding its icon back to the import** at the
       * top of this file — the block there is now exactly what the live menu draws.
       */
      // {
      //   key: 'agReport',
      //   to: '/reports/income-statement',
      //   icon: BarChart3,
      //   permission: P.REPORT_VIEW,
      //   module: MODULE.REPORTS,
      // },
    ],
  },
  {
    /*
     * **Cleaner** (docs/10 §50): its orders register, which is also where each job's
     * expenses are entered. The section report is generated under Reports below, like every
     * other business line's.
     */
    key: 'cleaner',
    icon: Sparkles,
    sections: [SECTION_SLUGS.CLEANER],
    alwaysGroup: true,
    items: [
      {
        key: 'clOrders',
        to: '/cleaner/orders',
        icon: Sparkles,
        permission: P.CL_ORDER_VIEW,
        module: MODULE.CLEANING,
      },
      /* §52 — everything in the section's books, its customers, and its own report. */
      {
        key: 'clExpenses',
        to: '/cleaner/expenses',
        icon: HandCoins,
        permission: P.CL_ORDER_VIEW,
        module: MODULE.CLEANING,
      },
      {
        key: 'clCustomers',
        to: '/cleaner/customers',
        icon: Users,
        permission: P.CL_ORDER_VIEW,
        module: MODULE.CLEANING,
      },
      {
        key: 'clReport',
        to: '/cleaner/report',
        icon: TrendingUp,
        permission: P.CL_ORDER_VIEW,
        module: MODULE.CLEANING,
      },
    ],
  },
  // {
  //   key: 'income',
  //   icon: FileText,
  //   items: [
  //     {
  //       key: 'incomeInvoices',
  //       to: '/invoices?kind=income',
  //       icon: FileText,
  //       permission: P.INVOICE_VIEW,
  //       module: MODULE.INVOICES,
  //     },
  //     {
  //       key: 'receipts',
  //       to: '/payments?kind=receipt',
  //       icon: HandCoins,
  //       permission: P.PAYMENT_VIEW,
  //       module: MODULE.PAYMENTS,
  //     },
  //     {
  //       // The financial view of the customer register (docs/10 §5.3) — balances per
  //       // currency and a statement. Editing a customer is still the party screen's job.
  //       key: 'customers',
  //       to: '/customers',
  //       icon: Users,
  //       permission: P.PARTY_VIEW,
  //       module: MODULE.PARTIES,
  //     },
  //     {
  //       key: 'arAging',
  //       to: '/reports/aging?kind=income',
  //       icon: Clock,
  //       permission: P.REPORT_VIEW,
  //       module: MODULE.INVOICES,
  //     },
  //   ],
  // },
  // {
  //   key: 'outcome',
  //   icon: ReceiptText,
  //   items: [
  //     {
  //       key: 'outcomeInvoices',
  //       to: '/invoices?kind=outcome',
  //       icon: ReceiptText,
  //       permission: P.INVOICE_VIEW,
  //       module: MODULE.INVOICES,
  //     },
  //     {
  //       key: 'disbursements',
  //       to: '/payments?kind=disbursement',
  //       icon: HandCoins,
  //       permission: P.PAYMENT_VIEW,
  //       module: MODULE.PAYMENTS,
  //     },
  //     {
  //       key: 'expenses',
  //       to: '/expenses',
  //       icon: Receipt,
  //       permission: P.EXPENSE_VIEW,
  //       module: MODULE.EXPENSES,
  //     },
  //     {
  //       key: 'vendors',
  //       to: '/parties?role=vendor',
  //       icon: Users,
  //       permission: P.PARTY_VIEW,
  //       module: MODULE.PARTIES,
  //     },
  //     {
  //       key: 'apAging',
  //       to: '/reports/aging?kind=outcome',
  //       icon: Clock,
  //       permission: P.REPORT_VIEW,
  //       module: MODULE.INVOICES,
  //     },
  //     {
  //       key: 'expenseByCategory',
  //       to: '/reports/expense-by-category',
  //       icon: PieChart,
  //       permission: P.REPORT_VIEW,
  //       module: MODULE.EXPENSES,
  //     },
  //   ],
  // },
  /*
   * ── The company's own affairs ─────────────────────────────────────────────────────
   *
   * **Group-level only, by construction.** The group carries no `sections`, so under the
   * narrowing rule (`isGroupVisible`) it appears at "All sections" and in HQ and nowhere
   * else — which is exactly where it was asked for: what the company buys and sells on its
   * own account, and what it spends, are questions about the whole group rather than about
   * one business line.
   *
   * Both rows point at screens that already exist and are already section-scoped (**I7**),
   * so a viewer sees the sections they hold and no more. Nothing here is a second register:
   * these are the same documents the sections write, read across all of them.
   */
  {
    key: 'company',
    icon: Landmark,
    items: [
      {
        /*
         * **Property the company buys and sells for itself** — the treat register
         * (docs/10 §4.2a). Its documents are filed under Real Estate, which owns the
         * module; this row is the group's *view* of them, which is the question an owner
         * asks: what did we buy, what did we sell it for, what was the profit.
         */
        key: 'companyProperties',
        to: '/treats',
        icon: Home,
        permission: P.RE_TREAT_VIEW,
        module: MODULE.TREATS,
      },
      {
        /*
         * **What the company spends**, across every section: the category, the amount and
         * its currency, the date, and who actually spent it (`Expense.spentByPartyId`).
         * The section-level Expenses rows remain under the finance groups they were
         * commented out of; this is the group's read of the same documents.
         */
        key: 'companyExpenses',
        to: '/expenses',
        icon: Coins,
        permission: P.EXPENSE_VIEW,
        module: MODULE.EXPENSES,
      },
    ],
  },
  {
    key: 'people',
    icon: Users,
    items: [
      {
        key: 'employees',
        to: '/payroll/employees',
        icon: Users,
        permission: P.PARTY_VIEW,
        module: MODULE.PARTIES,
      },
      {
        /*
         * **One Payroll entry, not two.**
         *
         * There were two rows here — "what the month costs" at `/payroll/summary` and the
         * generated runs at `/payroll` — and both rendered as the word *Payroll*, in all
         * three languages. Two identical menu entries do not read as two views of one
         * thing; they read as a bug, and choosing between them required already knowing
         * the difference. The two screens are now one (`PayrollListPage`) with a switch
         * that names what each view answers, so the menu can go back to naming the subject.
         */
        key: 'payrollRuns',
        to: '/payroll',
        icon: HandCoins,
        permission: P.PAYROLL_VIEW,
        module: MODULE.PAYROLL,
      },
      {
        /*
         * The same people measured by what they brought in. `anyOf` is deliberately *not*
         * used: the sheet states pay **and** reports documents, so both grants are needed
         * and the route enforces the pair — an item shown on either alone would be a link
         * that 403s.
         */
        key: 'employeeReport',
        to: '/payroll/report',
        icon: TrendingUp,
        permission: P.PAYROLL_VIEW,
        module: MODULE.PAYROLL,
      },
    ],
  },
  // {
  //   key: 'commissions',
  //   icon: Percent,
  //   items: [
  //     {
  //       key: 'commissionRegister',
  //       to: '/commissions',
  //       icon: Percent,
  //       permission: P.COMMISSION_VIEW,
  //       module: MODULE.COMMISSIONS,
  //     },
  //     {
  //       key: 'commissionStatement',
  //       to: '/commissions/statement',
  //       icon: FileSpreadsheet,
  //       permission: P.COMMISSION_VIEW,
  //       module: MODULE.COMMISSIONS,
  //     },
  //     {
  //       key: 'commissionPlans',
  //       to: '/commissions/plans',
  //       icon: SlidersHorizontal,
  //       permission: P.COMMISSION_PLAN_VIEW,
  //       module: MODULE.COMMISSIONS,
  //     },
  //   ],
  // },
  /*
   * ── The group's cash and books ────────────────────────────────────────────────────
   *
   * Treasury sits below the section's own work rather than above it, which is a change of
   * position and a change of meaning. There used to be a safe per section, so the box was
   * the first thing a section accountant reached for; V2 collapsed all five into **one
   * group-owned safe** (docs/10 §2), and a box that belongs to the group belongs with the
   * group's books, next to the ledger and the statements that read it.
   */
  // {
  //   key: 'treasury',
  //   icon: Wallet,
  //   items: [
  //     {
  //       key: 'safes',
  //       to: '/treasury/safes',
  //       icon: Wallet,
  //       permission: P.SAFE_VIEW,
  //       module: MODULE.TREASURY,
  //     },
  //     {
  //       key: 'safeStatement',
  //       to: '/treasury/statement',
  //       icon: FileSpreadsheet,
  //       permission: P.REPORT_VIEW,
  //       module: MODULE.TREASURY,
  //     },
  //     {
  //       key: 'reconciliation',
  //       to: '/treasury/reconciliation',
  //       icon: Scale,
  //       permission: P.SAFE_RECON_VIEW,
  //       module: MODULE.TREASURY,
  //     },
  //     {
  //       key: 'safeReport',
  //       to: '/treasury/safe-report',
  //       icon: Landmark,
  //       // Checked globally (docs/03 §2): a section treasurer who somehow held this must
  //       // still not get every other section's cash movement.
  //       permission: P.REPORT_CONSOLIDATED_VIEW,
  //       module: MODULE.TREASURY,
  //       global: true,
  //     },
  //   ],
  // },
  // {
  //   key: 'accounting',
  //   icon: BookOpen,
  //   items: [
  //     {
  //       key: 'accounts',
  //       to: '/accounting/accounts',
  //       icon: ListTree,
  //       permission: P.ACCOUNT_VIEW,
  //       module: MODULE.ACCOUNTING,
  //     },
  //     {
  //       key: 'journal',
  //       to: '/accounting/journal',
  //       icon: BookOpen,
  //       permission: P.JOURNAL_VIEW,
  //       module: MODULE.ACCOUNTING,
  //     },
  //     {
  //       key: 'periods',
  //       to: '/accounting/periods',
  //       icon: CalendarRange,
  //       permission: P.PERIOD_VIEW,
  //       module: MODULE.ACCOUNTING,
  //     },
  //   ],
  // },
  /*
   * ## Reports (docs/10 §16)
   *
   * Four things, in the order the office asked for them: **everything**, then **each
   * section**, then **the people**, then **the money going out**.
   *
   * What is *not* here is the block of financial statements this group used to be — trial
   * balance, income statement, balance sheet, cash flow, general ledger, account statement,
   * and the old consolidating General Report. They were the accountant's reports on a menu
   * every role opens, and the four below are the ones the business actually reads.
   *
   * They are **parked, not deleted** (see the block after this one). Every route still
   * resolves, so an auditor asking for a trial balance gets one at `/reports/trial-balance`,
   * and nothing that reconciles the books has been taken away — this is a menu decision,
   * which is what a menu is for.
   */
  {
    key: 'reports',
    icon: FileSpreadsheet,
    items: [
      {
        /*
         * **The General Report** — everything created in the system, every section, one list.
         *
         * Not `global`, and that is the difference from the report it replaced. The old one
         * was a *consolidation* — one figure standing for four sections — so holding its
         * grant inside a single section had to be treated as not holding it. This is a
         * **register**: a section accountant opening it reads their own section's documents,
         * which is the per-section report below arrived at from the other direction. The
         * service still demands `report.consolidated.view` from anyone whose scope actually
         * spans sections, so the group-wide view is gated exactly where it always was.
         */
        key: 'generalReport',
        to: '/reports/activity',
        icon: Activity,
        permission: P.REPORT_VIEW,
        module: MODULE.REPORTS,
      },

      /*
       * **One entry per section**, generated from `SECTIONS` rather than written out four
       * times — a fifth business line is a row in the constant, not four edits here and one
       * forgotten. `sectionSlug` makes the shell hide the entry from anybody who does not
       * hold that section, so a Brokers accountant sees Brokers and nothing else.
       */
      ...SECTION_REPORT_SLUGS.map((slug) => ({
        key: `sectionReport_${slug}`,
        to: `/reports/section/${slug}`,
        icon: SECTION_REPORT_ICONS[slug] ?? FileText,
        permission: P.REPORT_VIEW,
        module: MODULE.REPORTS,
        /* Hidden from anybody who does not hold the section — see `isItemVisible`. */
        sectionSlug: slug,
      })),

      /*
       * **There is no Employee Report row here, and that is the fix rather than the gap.**
       *
       * There were two, both reading *Employee Report*, in all three languages — one in this
       * group pointing at `/payroll/employees/kpi`, and one under People pointing at
       * `/payroll/report`. The first URL was never a route, so half the menu's own users
       * reached "Page not found" from a link the application drew for them.
       *
       * The duplicate is the same failure §5.1's menu had and for the same reason: two
       * identical entries do not read as two views of one thing, they read as a bug, and
       * choosing between them requires already knowing the difference. The surviving row is
       * under **People**, beside Employees and Payroll, because that is where somebody stands
       * when the question is about a person rather than about a section's books.
       *
       * `nav.test.jsx` now walks every `to` against the route table, so the next dead link is
       * a failing test rather than a screenshot.
       */
      {
        /*
         * **The expenses report** — every expense document, not a netted category total.
         * The by-category summary is still at `/reports/expense-by-category`; this is the
         * register behind it, which is the half the office could not reach.
         */
        key: 'expensesReport',
        to: '/reports/expenses',
        icon: Coins,
        permission: P.EXPENSE_VIEW,
        module: MODULE.EXPENSES,
      },
    ],
  },

  /*
   * ## The accounting reports, parked
   *
   * Off the menu (§16), routes untouched:
   *
   *   /reports/trial-balance      the proof the books balance — what an auditor asks for
   *   /reports/income-statement   the section P&L
   *   /reports/balance-sheet
   *   /reports/cash-flow
   *   /reports/general-ledger     the drill-down under every figure above
   *   /reports/account-statement
   *   /reports/general            the old consolidating General Report (P&L per section)
   *
   * Restoring any of them is uncommenting the entry below **and re-importing its icon** —
   * `ListTree`, `Scale` and `Waves` came out of the import list with the rows, because an
   * unused import is a lint error and a lint error nobody can fix is how a parked block
   * turns into a deleted one.
   *
   * Kept as code rather than as a sentence in a document because the two drift, and the
   * version that drifts silently is the one nobody is reading.
   */
  // {
  //   key: 'accounting-reports',
  //   icon: Scale,
  //   items: [
  //     { key: 'trialBalance', to: '/reports/trial-balance', icon: Scale,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     { key: 'incomeStatement', to: '/reports/income-statement', icon: TrendingUp,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     { key: 'balanceSheet', to: '/reports/balance-sheet', icon: Landmark,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     { key: 'cashFlow', to: '/reports/cash-flow', icon: Waves,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     { key: 'generalLedger', to: '/reports/general-ledger', icon: ListTree,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     { key: 'accountStatement', to: '/reports/account-statement', icon: FileSpreadsheet,
  //       permission: P.REPORT_VIEW, module: MODULE.REPORTS },
  //     // The consolidating General Report — `global: true`, because holding the
  //     // consolidated grant inside one section is not holding it.
  //     { key: 'consolidatedReport', to: '/reports/general', icon: Landmark,
  //       permission: P.REPORT_CONSOLIDATED_VIEW, module: MODULE.REPORTS, global: true },
  //   ],
  // },
  {
    key: 'settings',
    icon: Settings,
    items: [
      {
        key: 'users',
        to: '/settings/users',
        icon: Users,
        permission: P.USER_VIEW,
        module: MODULE.ADMIN,
        global: true,
      },
      {
        key: 'roles',
        to: '/settings/roles',
        icon: ShieldCheck,
        permission: P.ROLE_VIEW,
        module: MODULE.ADMIN,
        global: true,
      },
      {
        key: 'sections',
        to: '/settings/sections',
        icon: Building2,
        permission: P.SECTION_VIEW,
        module: MODULE.ADMIN,
        global: true,
      },
      {
        /*
         * Not `global: true`, unlike everything else in this group. `currency.view` is a
         * floor grant held in a *section* assignment — it is what a form needs to know how
         * many decimals an amount has — so checking it globally would hide the list from
         * every section accountant while leaving the route open to them.
         */
        key: 'currencies',
        to: '/settings/currencies',
        icon: Coins,
        permission: P.CURRENCY_VIEW,
        module: MODULE.DASHBOARD,
      },
    ],
  },
  {
    key: 'audit',
    icon: Activity,
    items: [
      {
        key: 'activity',
        to: '/audit',
        icon: Activity,
        permission: P.AUDIT_VIEW,
        module: MODULE.ADMIN,
        global: true,
      },
    ],
  },
];

const SECTION_SCOPED = new Set(SECTION_MODULE_KEYS);

/**
 * **Can this user step out of the section they are standing in?**
 *
 * The section narrowing (`isGroupVisible`) hides the shared groups while one business line
 * is selected, on the understanding that "All sections" is one click away. For somebody
 * holding a single section and no global scope it is not: the switcher does not render at
 * all (docs/03 §5), so narrowing would take their reports and their employees away with no
 * route back.
 *
 * Read from the user's **own grants** rather than from `useSections()`, and that is not
 * interchangeable. The sections query is a cache of what the server returned; the access
 * claim is what this person actually holds. Keying on the cache means a screen that seeded
 * or prefetched more sections than the user holds silently changes their menu, which is a
 * bug that only ever shows up as "why can't I see Reports?".
 */
export const canLeaveSectionFor = (access) =>
  !!access?.hasGlobalScope || (access?.sectionIds?.length ?? 0) > 1;

/**
 * Decide whether one item is visible.
 *
 * Two checkers, not one, and the difference matters. `can(p, sectionId)` answers "in
 * this section"; `canGlobal(p)` answers "at group level". A `global: true` item must use
 * the second — `can(p, null)` reads a null section as *"in any section I hold"*, which
 * would let a user granted `report.consolidated.view` in one section reach the whole
 * group's cash position.
 *
 * @param {object} item                 a navigation item
 * @param {{ can: Function, canGlobal: Function }} checkers
 * @param {object} context
 * @param {string|null} [context.activeSectionId]
 * @param {string[]|null} [context.modules]
 *   the module keys in force for the current scope — one section's, or the union across
 *   every section the user can see when they are looking at the group. `null` means
 *   "not known yet", and an unknown configuration must not blank the menu while the
 *   sections query is still in flight, so nothing is filtered.
 */
export const isItemVisible = (
  item,
  { can, canGlobal },
  { activeSectionId = null, modules = null, sectionSlugs = null } = {},
) => {
  /*
   * Module gate. Group-scoped modules are skipped: `admin` is not a section's to switch
   * off, and filtering it here would hide user management from an administrator the
   * moment they switched to a section.
   */
  if (item.module && SECTION_SCOPED.has(item.module) && modules && !modules.includes(item.module)) {
    return false;
  }

  /*
   * **An item that names one section is that section's** (docs/10 §16).
   *
   * The per-section reports are the case: four rows in a group that is not itself a
   * vertical, each pointing at one business line's register. A Brokers accountant offered
   * *Construction Report* would get `SECTION_FORBIDDEN` from a menu row — which is the
   * broken link the Phase 4 rule exists to prevent, only worse, because it looks like a
   * permissions bug rather than a menu that should never have offered it.
   *
   * `sectionSlugs` is what the caller can actually reach — `/sections` is scoped, so the
   * list is already theirs. **Null means the list has not loaded**, and the item shows: a
   * menu that hides rows while a query is in flight flickers, and the route behind this one
   * is scoped on the server anyway (I7). Fail *open* here is safe precisely because the
   * thing it guards is a link, not data.
   */
  if (item.sectionSlug && sectionSlugs && !sectionSlugs.includes(item.sectionSlug)) {
    return false;
  }

  const check = (permission) => (item.global ? canGlobal(permission) : can(permission, activeSectionId));

  if (item.anyOf?.length) return item.anyOf.some(check);
  if (!item.permission) return true; // e.g. the dashboard, which everyone signed in can see
  return check(item.permission);
};

/**
 * Does this group belong on screen, given where the user is standing?
 *
 * ## Standing in one business line shows *that business line*
 *
 * Picking Brokers means working on Brokers, and a menu that answers with Brokers plus
 * Employees plus Commissions plus Reports plus Settings is answering a question nobody
 * asked. So a selected section shows its own group and the shell's, and nothing else; the
 * shared screens are group-level work and live under **All sections** and HQ, where the
 * question they answer ("across the whole company") is the one being asked.
 *
 * Three cases deliberately show everything instead:
 *
 *   - **the group view** (`sectionSlug` null, no section picked) — a CFO at group level does
 *     run all four business lines, and the shared screens are exactly what they came for;
 *   - **HQ**, for the same reason the module registry exempts it: the group's own books are
 *     assembled there, so anything it cannot reach is a hole in the picture;
 *   - **a user who cannot leave the section they are in.** Somebody holding one section and
 *     no global scope is never shown a switcher (docs/03 §5), so for them the section *is*
 *     the application — narrowing it would take away their reports and their employees with
 *     no route back. The narrowing exists to stop one business line's menu being cluttered
 *     by the others', and that reason does not apply to somebody who only has one.
 *
 * `sectionSlug` is also null while the sections query is in flight, which lands on the first
 * case — the menu must not blank on boot.
 */
export const isGroupVisible = (group, { sectionSlug = null, canLeaveSection = true } = {}) => {
  const inOneSection = !!sectionSlug && sectionSlug !== SECTION_SLUGS.HQ && canLeaveSection;

  if (inOneSection) {
    /* `always` is the shell's own — the dashboard, which is where every section starts. */
    return group.always === true || !!group.sections?.includes(sectionSlug);
  }

  if (!group.sections) return true;
  if (!sectionSlug || sectionSlug === SECTION_SLUGS.HQ) return true;
  return group.sections.includes(sectionSlug);
};

/**
 * The tree the sidebar actually renders: groups that belong to another business line are
 * dropped whole, and groups with no visible item are dropped too, so a role never sees an
 * empty heading.
 */
export const visibleNavigation = (checkers, context) =>
  NAVIGATION.filter((group) => isGroupVisible(group, context ?? {}))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isItemVisible(item, checkers, context)),
    }))
    .filter((group) => group.items.length > 0);

/**
 * Flat list of routable items — used by the ⌘K palette.
 *
 * `groupSections` rides along because the palette is a second door onto the same rows, and
 * opening a vertical's screen has to stand the user in that vertical whichever door they
 * came through. Without it ⌘K → "Agency Invoices" would file the document under whatever
 * the switcher happened to be on, which is the exact trap the sidebar stopped setting.
 */
export const flatNavigation = (checkers, context) =>
  visibleNavigation(checkers, context).flatMap((group) =>
    group.items.map((item) => ({ ...item, groupKey: group.key, groupSections: group.sections ?? null })),
  );

export default NAVIGATION;
